/**
 * WebSocket Constants for Real-time Communication
 * 
 * WebSocket is used for real-time notifications and updates FROM the backend:
 * - New message notifications
 * - Typing indicators from other users  
 * - Presence updates
 * - Post updates (likes, comments, approvals)
 * - Bulk operation progress
 * - System notifications
 * 
 * And for specific client-to-server messages:
 * - Presence check requests
 */

// ==================== WEBSOCKET CONFIGURATION ====================
export const WEBSOCKET_CONFIG = {
  // Connection endpoints
  STOMP_ENDPOINT: '/ws',
  RECONNECT_DELAY: 5000,
  MAX_RECONNECT_ATTEMPTS: 10,
  HEARTBEAT: {
    OUTGOING: 10000,
    INCOMING: 10000
  }
};

// How often a connected client tells the server it is still here. The server expires presence after 5 minutes.
export const PRESENCE_HEARTBEAT_INTERVAL_MS = 30000;

// ==================== SENDING DESTINATIONS ====================

// Client-to-server destinations
export const APP_DESTINATIONS = {
  PRESENCE_SUBSCRIBE: '/app/presence.subscribe',
  PRESENCE_UNSUBSCRIBE: '/app/presence.unsubscribe',
  PRESENCE_HEARTBEAT: '/app/presence.heartbeat',
  TYPING: '/app/chat.typing' //Used in Conversation updates
};

// ==================== SUBSCRIPTION DESTINATIONS ====================


// User-specific destinations (private queues)
export const QUEUES = {
  
  // Personal conversation updates (messages, typing, reactions)
  CONVERSATION_UPDATES: '/user/queue/conversation-updates', // Verified with notification-service
  
  // Personal conversation updates (unread counts, last messages)
  COUNTERS: '/user/queue/counters', // Verified with notification-service
  
  // Personal presence status
  PRESENCE: '/user/queue/presence', // Verified with notification-service
  
  // Bulk operation progress
  BULK_PROGRESS: '/user/queue/bulk.progress', // Verified with notification-service
  BULK_COMPLETE: '/user/queue/bulk.complete', // Verified with notification-service
  BULK_ERROR: '/user/queue/bulk.error', // Verified with notification-service
  
  // Personal notifications
  NOTIFICATIONS: '/user/queue/notifications', // Verified with notification-service
  
  // Personal feed updates (targeted notifications)
  FEED_UPDATES: '/user/queue/feed-updates', // Verified with notification-service
  
  // Comment-specific updates (likes, replies, etc.)
  COMMENT_UPDATES: '/user/queue/feed-updates/comments' // Verified with notification-service
};

// ==================== RECEIVE-ONLY WEBSOCKET ====================
// 
// NOTE: This WebSocket connection is RECEIVE-ONLY for security and complexity reasons.
// All client-to-server communication should use REST API endpoints instead.
// WebSocket is used only for real-time notifications and updates FROM the backend.

// ==================== EVENT TYPES ====================

// Message events
export const MESSAGE_EVENTS = {
  NEW_MESSAGE: 'NEW_MESSAGE',
  MESSAGE_READ: 'MESSAGE_READ',
  MESSAGE_DELETED: 'MESSAGE_DELETED',
  MESSAGE_REACTION: 'MESSAGE_REACTION',
  REACTION_ADDED: 'REACTION_ADDED',
  REACTION_REMOVED: 'REACTION_REMOVED',
  TYPING_START: 'TYPING_START',
  TYPING_STOP: 'TYPING_STOP',

  // Message moderation events
  SENDER_MESSAGE_APPROVED: 'SENDER_MESSAGE_APPROVED',
  PARTICIPANT_MESSAGE_APPROVED: 'PARTICIPANT_MESSAGE_APPROVED',
  SENDER_MESSAGE_REJECTED: 'SENDER_MESSAGE_REJECTED',
  PARTICIPANT_MESSAGE_REJECTED: 'PARTICIPANT_MESSAGE_REJECTED',
  
  // Bulk message moderation events (summary for mass messages)
  BULK_MESSAGES_APPROVED: 'BULK_MESSAGES_APPROVED',
  BULK_MESSAGES_REJECTED: 'BULK_MESSAGES_REJECTED',

  // Bulk job media processing state — fires when Step Functions finishes
  BULK_JOB_MEDIA_PROCESSED: 'BULK_JOB_MEDIA_PROCESSED',
  // Message purchase events
  MESSAGE_UNLOCKED: 'MESSAGE_UNLOCKED',
};

// Feed events (posts, likes, comments)
export const FEED_EVENTS = {
  POST_CREATED: 'POST_CREATED',
  POST_APPROVED: 'POST_APPROVED',
  POST_LIKED_FEED: 'POST_LIKED_FEED',
  POST_UNLIKED: 'POST_UNLIKED',
  POST_COMMENTED_FEED: 'POST_COMMENTED_FEED', // Comment count updates for feed
  POST_COMMENT_ADDED: 'POST_COMMENT_ADDED', // Actual comment data
  POST_RESHARED: 'POST_RESHARED', // Echo count updates
  COMMENT_LIKED: 'COMMENT_LIKED',
  COMMENT_UNLIKED: 'COMMENT_UNLIKED'
};

// Bulk operation events
export const BULK_EVENTS = {
  BULK_PROGRESS: 'BULK_PROGRESS',
  BULK_COMPLETE: 'BULK_COMPLETE',
  BULK_ERROR: 'BULK_ERROR',
};

// Conversation event types
export const CONVERSATION_EVENTS = {
  PARTICIPANT_ADDED: 'PARTICIPANT_ADDED',
  PARTICIPANT_REMOVED: 'PARTICIPANT_REMOVED',
  CONVERSATION_UPDATED: 'CONVERSATION_UPDATED'
};

// ==================== CONNECTION STATES ====================

export const CONNECTION_STATES = {
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
  ERROR: 'ERROR'
};

// ==================== EXPORTS ====================

const webSocketConstants = {
  WEBSOCKET_CONFIG,
  QUEUES,
  MESSAGE_EVENTS,
  FEED_EVENTS,
  BULK_EVENTS,
  CONNECTION_STATES,
  APP_DESTINATIONS,
  CONVERSATION_EVENTS
};

export default webSocketConstants;
